/*
 Simple structured logger for both server (Node) and browser.
 Features:
 - Levels: debug, info, warn, error
 - Environment-based level (LOG_LEVEL) and pretty toggle (LOG_PRETTY)
 - Request-scoped context support (e.g., requestId)
 - Safe JSON serialization with fallback
*/

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const levelWeights: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

function getEnvVar(name: string): string | undefined {
  try {
    if (typeof process !== 'undefined' && process.env) {
      return (process.env as Record<string, string | undefined>)[name];
    }
  } catch (_) {
    // ignore for browsers without process
  }
  return undefined;
}

const DEFAULT_LEVEL: LogLevel = (getEnvVar('LOG_LEVEL') as LogLevel) || 'info';
const PRETTY = /^(1|true|yes)$/i.test(getEnvVar('LOG_PRETTY') || '1');

export interface LogContext {
  requestId?: string;
  userId?: string | number;
  [key: string]: unknown;
}

export interface LoggerOptions {
  level?: LogLevel;
  context?: LogContext;
}

function safeSerialize(value: unknown) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return String(value);
  }
}

export class Logger {
  private level: LogLevel;
  private context: LogContext;

  constructor(options: LoggerOptions = {}) {
    this.level = options.level || DEFAULT_LEVEL;
    this.context = options.context || {};
  }

  public child(context: LogContext): Logger {
    return new Logger({ level: this.level, context: { ...this.context, ...context } });
  }

  public setLevel(level: LogLevel) {
    this.level = level;
  }

  private shouldLog(level: LogLevel): boolean {
    return levelWeights[level] >= levelWeights[this.level];
  }

  private format(level: LogLevel, message: string, meta?: unknown) {
    const timestamp = new Date().toISOString();
    const payload: Record<string, unknown> = {
      timestamp,
      level,
      ...this.context,
    };
    if (meta !== undefined) payload.meta = safeSerialize(meta);

    if (PRETTY && typeof window === 'undefined') {
      return `${timestamp} [${level.toUpperCase()}] ${message} ${JSON.stringify(payload)}`;
    }
    return payload;
  }

  private write(level: LogLevel, message: string, meta?: unknown) {
    if (!this.shouldLog(level)) return;
    const formatted = this.format(level, message, meta);
    const method = level === 'debug' ? 'log' : level;

    (console as any)[method](formatted);
  }

  public debug(message: string, meta?: unknown) {
    this.write('debug', message, meta);
  }

  public info(message: string, meta?: unknown) {
    this.write('info', message, meta);
  }

  public warn(message: string, meta?: unknown) {
    this.write('warn', message, meta);
  }

  public error(message: string, meta?: unknown) {
    this.write('error', message, meta);
  }
}

// Default singleton logger
export const logger = new Logger();
